/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, useTheme } from '@mui/material';
import React from 'react';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import { getBaseLayers, getProjection } from './utils';
import type { ProductConfig } from '../../types';
import MapViewGeoJson from '../MapViewGeoJson/MapViewGeoJson';

interface ProducFormLayoutProps {
  children: React.ReactNode;
  mapChildren?: React.ReactNode;
  productConfig: ProductConfig;
  geoJSONLayers: OpenLayersLayerProps[];
}

const ProductFormLayout: React.FC<ProducFormLayoutProps> = ({
  children,
  mapChildren,
  productConfig,
  geoJSONLayers = [],
}: ProducFormLayoutProps) => {
  const theme = useTheme();
  const baseLayers = getBaseLayers(productConfig);
  const projection = getProjection(productConfig);

  return (
    <Grid
      container
      sx={{
        height: '100%',
        overflowY: 'auto',
        [theme.breakpoints.up('sm')]: {
          overflowY: 'hidden',
        },
      }}
    >
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 8,
        }}
        sx={{
          height: 400,
          padding: 2,
          [theme.breakpoints.up('sm')]: {
            height: '100%',
            padding: 0,
          },
        }}
      >
        <MapViewGeoJson
          baseLayers={baseLayers}
          geoJSONLayers={geoJSONLayers}
          projection={projection}
        >
          {mapChildren}
        </MapViewGeoJson>
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          lg: 4,
        }}
        sx={{
          paddingTop: 5,
          paddingBottom: 5,
          paddingRight: 2,
          [theme.breakpoints.up('sm')]: {
            overflowY: 'auto',
            height: '100%',
          },
        }}
      >
        {children}
      </Grid>
    </Grid>
  );
};

export default ProductFormLayout;
